import type { InputRouter, LayerHandle } from "../../ui/input/router.ts";
import type { TipController } from "../bar/tips.ts";
import { pageKeys, type KeyTable } from "../controls/page.ts";
import { actionPress } from "../controls/press.ts";
import type { WindowOrder } from "../windows/order.ts";
import type { BagStore } from "./store.ts";

/**
 * The bag's keys (P2.8-T11, ADR-091 decision 21), named here by their defaults; the key is the one the page's key
 * table binds Inventory to (P1.9-T50, `controls/`), read on each press. I opens and closes the Inventory window while the game owns the
 * keyboard, so never while a text field has focus or a modal holds the keys. While the window is open it is a layer
 * of the input router that names no key, so Escape closes it as it closes any topmost layer and every other key
 * reaches the game as though nothing were open (ADR-061's amendment "a surface that is not modal never takes the
 * game's keys"): the character still moves, fights and uses keys 1 to 0. I is heard as a game key, not as the
 * layer's, so it closes the window whichever layer is topmost.
 *
 * I leaves the keyboard's focus on the game, so Enter still opens chat and Tab still selects a target; Shift+I
 * opens the window, or enters one already open, with focus on its close button, and never closes it (ADR-061's
 * amendment "a play window never stops play").
 *
 * The router asks an open layer to close before it asks the page's tooltip (`InputRouter.onEscape` is heard only with
 * no layer open), so the layer itself gives Escape to a showing tooltip first: that press closes the tooltip and the
 * window stays; the next closes the window. A slot picked up to be moved takes Escape the same way, after a tooltip:
 * it is put back and the window stays. Closing the window drops a tooltip one of its slots held.
 *
 * Where the page has a second play window the two share an order (`windows/order.ts`; ADR-061's amendment "a window
 * can be moved", item 8): the window comes to the front as it opens, and whenever it is raised its layer is opened
 * again, which makes it the router's topmost, so Escape closes the window drawn in front first.
 */

/**
 * Binds the bag's keys to the page's input router and the bag store.
 *
 * @param store The bag store, whose open state the layer follows.
 * @param input The page's input router.
 * @param tips The page's tooltip controller, or null where nothing shows tooltips.
 * @param order The play windows' order, or undefined on a page with one play window.
 * @param keys The key table, whose Inventory key opens and closes the window; the page's unless given.
 * @returns A function that unbinds the key and closes the window's layer.
 * @example
 * bindBagKeys(bag, input, tips);
 */
export function bindBagKeys(store: Pick<BagStore, "subscribe" | "getSnapshot" | "setOpen" | "toggle" | "enter" | "putBack">, input: Pick<InputRouter, "open" | "onKey">, tips: Pick<TipController, "escape" | "getSnapshot" | "clear"> | null, order?: Pick<WindowOrder, "subscribe" | "raise" | "front">, keys: Pick<KeyTable, "actionOf"> = pageKeys): () => void {
  let layer: LayerHandle | null = null;

  /**
   * Opens the window's layer, which Escape closes: a showing tooltip takes that press and the layer opens again; a slot
   * picked up to be moved is put back and the layer opens again (P2.8-T14); otherwise the window closes. A close this
   * binding asked for itself, which forgets the layer first, does none of these.
   */
  const openLayer = (): void => {
    layer = input.open({
      onClose: () => {
        if (layer === null) {
          return;
        }
        layer = null;
        if (tips?.escape() === true) {
          openLayer();
        } else if (store.getSnapshot().selected !== null) {
          openLayer();
          store.putBack();
        } else {
          store.setOpen(false);
        }
      },
    });
  };

  /**
   * Makes the layer follow the store's open state, dropping a bag slot's tooltip as the window closes.
   */
  const follow = (): void => {
    const { open } = store.getSnapshot();
    if (open && layer === null) {
      openLayer();
      order?.raise("inventory");
    } else if (!open) {
      if (tips?.getSnapshot()?.anchor.surface === "bag") {
        tips.clear();
      }
      if (layer !== null) {
        const opened = layer;
        layer = null;
        opened.close();
      }
    }
  };

  /**
   * Makes the layer the router's topmost when the window was raised over another.
   */
  const raised = (): void => {
    if (layer !== null && order?.front("inventory") === true) {
      const opened = layer;
      layer = null;
      opened.close();
      openLayer();
    }
  };
  const unsubscribe = store.subscribe(follow);
  const unorder = order?.subscribe(raised);
  const unlisten = input.onKey((event) => {
    if (event.repeat || event.defaultPrevented || !actionPress(keys, "inventory", event) || event.ctrlKey === true || event.altKey === true || event.metaKey === true) {
      return;
    }
    event.preventDefault();
    if (event.shiftKey === true) {
      store.enter();
    } else {
      store.toggle();
    }
  });
  follow();
  return () => {
    unsubscribe();
    unorder?.();
    unlisten();
    const opened = layer;
    layer = null;
    opened?.close();
  };
}
